import {useMemo} from 'react';
import {useGetDepartmentsInSummary} from '../../../core/repositories/reportSummaryRepository';
import {useGetReportList} from '../../../core/repositories/reportsRepository';

type Args = {
  companyID?: number;
};

/**
 * 企業IDが有効かどうかを判定
 */
const isValidCompanyId = (companyID?: number): companyID is number => {
  return !!companyID && companyID > 0;
};

/**
 * 体験記のデータを持っているかどうかを判定するフック
 *
 * @param companyID - 企業ID
 * @returns hasReports - 体験記データの有無
 * @returns hasAiReportSummary - AI選考対策のデータの有無
 */
export const useHasReportData = ({companyID}: Args) => {
  const isEnabled = isValidCompanyId(companyID);

  // 体験記データ取得
  const {data: reportData} = useGetReportList({
    queryParams: {
      companyIDs: companyID ?? 0,
      limit: 1,
      page: 1,
    },
    config: {
      enabled: isEnabled,
    },
  });

  const hasReports = useMemo(() => {
    if (!isValidCompanyId(companyID)) return false;
    if (!reportData?.ok) return false;
    return reportData.value.reports.length > 0;
  }, [companyID, reportData]);

  // AI選考対策の部門データ取得
  // reportTypeを指定しないことで、選考・インターン両方の部門データを取得
  const {data: departmentsData} = useGetDepartmentsInSummary({
    companyID: companyID ?? 0,
    queryParams: {},
    config: {
      enabled: isEnabled,
    },
  });

  const hasAiReportSummary = useMemo(() => {
    if (!isValidCompanyId(companyID)) return false;
    if (!departmentsData?.ok) return false;
    return (departmentsData.value.departments?.length ?? 0) > 0;
  }, [companyID, departmentsData]);

  return {
    hasReports,
    hasAiReportSummary,
  };
};
